'use client';

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { resolveRingProFreeSizeShipByDate } from 'content/products/ring-pro';
import type { ShippingEstimateObject } from 'components/ring-air/utils/types';
import { ringProUsAsset } from '../ringProUsAsset';
import styles from './RingProUsFit.module.css';

/* Framed photos from Figma 2847:2385 (know) and 2847:2386 (kit). */
const knowOrder = ringProUsAsset('fit/know-order.png');
const kitOrder = ringProUsAsset('fit/kit-order.png');
const kitSize = ringProUsAsset('fit/kit-size.png');
const ships = ringProUsAsset('fit/ships.png');
const arrives = ringProUsAsset('fit/arrives.png');

type Step = {
  id: string;
  image: string;
  when: 'today' | 'shipsBy' | 'est' | 'youChoose' | 'daysLater';
};

const PATHS: Record<'know' | 'kit', Step[]> = {
  know: [
    { id: 'order', image: knowOrder, when: 'today' },
    { id: 'ships', image: ships, when: 'shipsBy' },
    { id: 'arrives', image: arrives, when: 'est' },
  ],
  kit: [
    { id: 'order', image: kitOrder, when: 'today' },
    { id: 'confirm', image: kitSize, when: 'youChoose' },
    { id: 'ships', image: ships, when: 'shipsBy' },
    { id: 'ringArrives', image: arrives, when: 'est' },
  ],
};

type PathId = keyof typeof PATHS;
const PATH_IDS: PathId[] = ['know', 'kit'];

type RingProUsFitProps = {
  estimates?: ShippingEstimateObject | null;
};

/**
 * "Fit first. Then ring." (Figma 2535:12435): a centred header, a two-option
 * pill switch, and the chosen path as a row of step cards — photo, numbered
 * badge with a fading timeline rule, date, title, line. The row plays in once
 * it scrolls into view (photo wipe, badge, rule drawing left to right, copy),
 * and replays on every switch.
 */
export const RingProUsFit = ({ estimates }: RingProUsFitProps) => {
  const t = useTranslations('homepage');
  const [path, setPath] = useState<PathId>('know');
  const [play, setPlay] = useState(false);
  const [dated, setDated] = useState(false);
  const [dates, setDates] = useState<{ ships: string; arrives: string } | null>(
    null,
  );
  const sectionRef = useRef<HTMLElement | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const k = (key: string) => `homepage.ringProUs.fit.${key}`;

  // Dates resolve on the client so SSR never bakes in the build day.
  useEffect(() => {
    const base = new Date();
    const shipDate = resolveRingProFreeSizeShipByDate(
      estimates ?? undefined,
      true,
      base,
    );
    const arrivesDate = new Date(shipDate.getTime() + 2 * 86_400_000);

    const format = (d: Date) =>
      new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'Asia/Kolkata',
      }).format(d);

    setDates({
      ships: format(shipDate),
      arrives: format(arrivesDate),
    });
    setDated(true);
  }, [estimates]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setPlay(true);
        io.disconnect();
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const steps = PATHS[path];

  const whenLabel = (step: Step) => {
    if (
      step.when === 'today' ||
      step.when === 'youChoose' ||
      step.when === 'daysLater'
    ) {
      return t(k(`when.${step.when}`));
    }
    if (!dated || !dates) return '';
    if (step.when === 'shipsBy') {
      return t(k('when.shipsBy'), { date: dates.ships });
    }
    if (step.when === 'est') {
      return t(k('when.est'), { date: dates.arrives });
    }
    return '';
  };

  const onKey = (event: KeyboardEvent<HTMLButtonElement>, i: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const next = (i + 1) % PATH_IDS.length;
    setPath(PATH_IDS[next]!);
    tabRefs.current[next]?.focus();
  };

  return (
    <section
      ref={sectionRef}
      className={styles.section}
      aria-labelledby="ring-pro-us-fit-title"
    >
      <div className={styles.inner}>
        <header className={styles.head}>
          <h2 id="ring-pro-us-fit-title" className={styles.title}>
            {t(k('title'))}
          </h2>
          <p className={styles.sub}>{t(k('sub'))}</p>

          <div
            className={styles.seg}
            role="tablist"
            aria-label={t(k('aria.paths'))}
          >
            {PATH_IDS.map((id, i) => (
              <button
                key={id}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                id={`ringpro-fit-tab-${id}`}
                aria-selected={path === id}
                aria-controls="ringpro-fit-panel"
                tabIndex={path === id ? 0 : -1}
                className={styles.segBtn}
                onClick={() => setPath(id)}
                onKeyDown={(event) => onKey(event, i)}
              >
                {t(k(`paths.${id}`))}
              </button>
            ))}
          </div>
        </header>

        <ol
          key={path}
          id="ringpro-fit-panel"
          role="tabpanel"
          aria-labelledby={`ringpro-fit-tab-${path}`}
          className={styles.row}
          data-play={play ? 'true' : undefined}
        >
          {steps.map((step, i) => (
            <li
              key={step.id}
              className={styles.step}
              style={{ '--i': i } as CSSProperties}
            >
              <span className={styles.media}>
                <CustomImage
                  className={styles.img}
                  src={step.image}
                  alt={t(k(`steps.${path}.${step.id}.alt`))}
                  fill
                  sizes="(min-width: 992px) 33vw, 100vw"
                />
              </span>

              <span className={styles.rail} aria-hidden>
                <span className={styles.badge}>{i + 1}</span>
                <span className={styles.rule} />
              </span>

              <span className={styles.copy}>
                <span className={styles.when}>{whenLabel(step) || ' '}</span>
                <span className={styles.stepTitle}>
                  {t(k(`steps.${path}.${step.id}.title`))}
                </span>
                <span className={styles.stepBody}>
                  {t(k(`steps.${path}.${step.id}.body`))}
                </span>
              </span>
            </li>
          ))}
        </ol>

        <div className={styles.ctaWrap}>
          <CustomLink
            href="/ring-pro/buy"
            className={styles.cta}
            data-buttontype="ring_pro_us_fit_buy_button"
          >
            {t('homepage.ringProUs.cta.buy')}
          </CustomLink>
        </div>
      </div>
    </section>
  );
};

export default RingProUsFit;
